import { Box, Checkbox, FormControlLabel, Grid, TextField, Theme, useTheme } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { ReactNode } from 'react';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import moment from 'moment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { hasBannedCharacters } from '../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import GhostButton from '../shared/button/GhostButton';
import PrimaryButton from '../shared/button/PrimaryButton';
import { Subheading } from '../shared/typography/PrimaryTypography';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

export type ConfigEntityFormType = 'create' | 'edit';

type ConfigEntityInputType =
  | 'text'
  | 'select'
  | 'date'
  | 'month_year'
  | 'node'
  | 'number'
  | 'boolean';

export interface ConfigEntityInput<T extends Record<string, unknown>> {
  id: Extract<keyof T, string>;
  type: ConfigEntityInputType;
  label: string;
  default_value?: unknown;
  optional?: boolean;
  ignore_banned_characters?: boolean;
  hide_input?: boolean;
  helper_text?: string;
  inputNodeFactory?: (handleInputChange: (input_id: string, value: unknown) => void) => ReactNode;
  customInputValidation?: (input_value: unknown) => boolean;
  onControlledInputChange?: (input_value: unknown) => void;
}

interface ConfigEntityFormProps<T extends Record<string, unknown>> {
  form_title?: string;
  form_icon?: ReactNode;
  inputs: Array<ConfigEntityInput<T>>;
  controlled_entity?: T;
  grid_spacing?: number;
  confirm_button_label?: string;
  handleClose: () => void;
  handleConfirmInput: ((entity_data: T) => Promise<void>) | null;
}

const SOURCE = 'ConfigEntityForm.tsx';

/**
 * Generic form component for rendering dynamic input fields based on entity schema.
 * @param {string | undefined} form_title Optional title of the form to be shown at the top.
 * @param {ReactNode | undefined} form_icon Optional icon to show next to title at the top of form.
 * @param {Array<EntityInput>} inputs Configuration of inputs fields to be filled out for entity.
 * @param {T | undefined} controlled_entity Optional version of the entity being mutated in parent.
 * @param {number | undefined} grid_spacing Optional spacing to add between each input field component.
 * @param {string | undefined} confirm_button_label Optionally set text for confirmation button.
 * @param {(): void} handleClose Close the form.
 * @param {((): void) | null} handleConfirmInput Submit the input form data to the entity mutator function.
 * @returns {JSX.Element}
 */
const ConfigEntityForm = <T extends Record<string, unknown>>({
  form_title,
  form_icon,
  inputs,
  controlled_entity,
  grid_spacing,
  confirm_button_label,
  handleClose,
  handleConfirmInput
}: ConfigEntityFormProps<T>) => {
  // Default form state for inputs.
  const initial_input_values: T = Object.fromEntries(
    inputs.map((input: ConfigEntityInput<T>) => [input.id, input.default_value ?? null])
  ) as T;
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  // Internal state fallback.
  const [internalInputValuesMap, setInternalInputValuesMap] =
    React.useState<T>(initial_input_values);
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  // Checks if every non-optional input has a value (at least 1 option for array inputs), and no banned characters for text inputs.
  const hasValidInputs = (): boolean => {
    const inputs_valid: boolean = inputs.every((input: ConfigEntityInput<T>) => {
      const input_value: unknown = controlled_entity
        ? controlled_entity[input.id]
        : internalInputValuesMap[input.id];

      const is_required_valid = Boolean(
        input.optional === true ||
        (input_value !== undefined &&
          input_value !== null &&
          input_value !== '' &&
          (!Array.isArray(input_value) || input_value.length > 0))
      );

      const has_no_banned_characters = Boolean(
        input.ignore_banned_characters ||
        typeof input_value !== 'string' ||
        !hasBannedCharacters(input_value)
      );

      const passes_custom_validation = Boolean(
        input.customInputValidation === undefined || input.customInputValidation(input_value)
      );

      return is_required_valid && has_no_banned_characters && passes_custom_validation;
    });

    return inputs_valid;
  };

  // Handle updating input value internally, and in parent if controlled.
  const handleInputChange = (input_id: string, input_value: unknown): void => {
    const input: ConfigEntityInput<T> | undefined = inputs.find(
      (input: ConfigEntityInput<T>) => input.id === input_id
    );
    if (!input) return;
    setInternalInputValuesMap({ ...internalInputValuesMap, [input_id]: input_value });
    input.onControlledInputChange && input.onControlledInputChange(input_value);
  };

  const handleConfirmClick = async (): Promise<void> => {
    setConfirmLoading(true);
    try {
      if (handleConfirmInput) {
        await handleConfirmInput(controlled_entity ?? internalInputValuesMap);
      }
      handleClose();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'invalid config input') {
        return;
      }
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleConfirmClick'),
          {
            internalInputValuesMap: internalInputValuesMap,
            controlled_entity: controlled_entity
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to save entity', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setConfirmLoading(false);
    }
    return;
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={grid_spacing ? grid_spacing : 6}>
          {(form_title ?? form_icon) != null && (
            <Grid size={10}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  color: theme.palette.primary.main
                }}
              >
                {form_icon && form_icon}
                <Subheading weight="Medium" sx={{ mx: 2 }}>
                  {form_title}
                </Subheading>
              </Box>
            </Grid>
          )}
          {inputs
            .filter((input: ConfigEntityInput<T>) => !input.hide_input)
            .map((input: ConfigEntityInput<T>) => {
              const input_value: unknown = controlled_entity
                ? controlled_entity[input.id]
                : internalInputValuesMap[input.id];
              return (
                <Grid size={12} key={input.id}>
                  {input.type === 'text' && (
                    <TextField
                      error={
                        !input.ignore_banned_characters &&
                        typeof input_value === 'string' &&
                        input_value !== '' &&
                        hasBannedCharacters(input_value)
                      }
                      fullWidth
                      id={input.id}
                      label={input.label}
                      value={input_value ? input_value : ''}
                      variant="standard"
                      helperText={input.helper_text}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        handleInputChange(input.id, event.target.value);
                      }}
                    />
                  )}
                  {input.type === 'number' && (
                    <TextField
                      type="number"
                      fullWidth
                      id={input.id}
                      label={input.label}
                      defaultValue={
                        input.default_value ? input.default_value : input_value ? input_value : ''
                      }
                      helperText={input.helper_text}
                      variant="standard"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        handleInputChange(input.id, event.target.value);
                      }}
                    />
                  )}
                  {input.type === 'date' && (
                    <DatePicker
                      label={input.label}
                      defaultValue={
                        input.default_value
                          ? input.default_value
                          : input_value && input_value instanceof Date
                            ? moment(input_value)
                            : null
                      }
                      onChange={(new_date) => handleInputChange(input.id, new_date)}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'month_year' && (
                    <DatePicker
                      views={['year', 'month']}
                      label={input.label}
                      defaultValue={
                        input.default_value
                          ? input.default_value
                          : input_value && input_value instanceof Date
                            ? moment(input_value)
                            : null
                      }
                      onChange={(new_date) => handleInputChange(input.id, new_date)}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'boolean' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={input_value ? true : false}
                          value={input_value ? true : false}
                          color="primary"
                          onChange={(event) => {
                            handleInputChange(input.id, event.target.checked);
                          }}
                        />
                      }
                      label={input.label}
                    />
                  )}
                  {input.type === 'node' &&
                    input.inputNodeFactory &&
                    input.inputNodeFactory(handleInputChange)}
                </Grid>
              );
            })}

          <Grid size={12} alignItems={'right'}>
            <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
              <GhostButton
                startIcon={<ChevronLeftIcon />}
                dark
                onClick={() => {
                  handleClose();
                }}
              >
                Back
              </GhostButton>
              <PrimaryButton
                disabled={!hasValidInputs()}
                loading={confirmLoading}
                onClick={async () => {
                  await handleConfirmClick();
                }}
              >
                {confirm_button_label ?? 'Save'}
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ConfigEntityForm;
